/**
 * AppLayout - Responsive shell.
 *
 * - <md (mobile/iOS): AppHeader (avatar/logo/messages) + Outlet + BottomTabBar
 *   (Home, Search, Notifications, Store) + MobileDrawer + MessagesOverlay + PostOverlay + StoreItemOverlay (all slide-in). The mobile
 *   ComposeFab is rendered by the Home and Profile routes only — the "+ add
 *   post" action is scoped to those surfaces (see
 *   `components/PostFeed/ComposeFab`).
 * - md (tablet, >=768px): AppHeader + icon-only LeftSidebar + Outlet.
 * - lg (desktop, >=1024px): AppHeader + LeftSidebar (with labels) + Outlet + RightSidebar.
 *
 * AppLayout also owns the mobile PostOverlay state and exposes
 * `openPost(postId)` via `PostOverlayContext` so any surface that lists posts
 * (feed, search, notifications, etc.) can open the overlay without plumbing
 * state manually. Desktop viewports bypass the overlay and navigate to
 * `/posts/:id`.
 *
 * Store grid uses `StoreOverlayContext` the same way: mobile opens
 * `StoreItemOverlay` without changing the URL; md+ keeps `/store/:id` navigation.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { setDailyStats } from '../../store/aura.reducer';
import { getUserDailyPointStats } from '../../helpers/auraHelpers';
import { PostOverlayProvider } from '../../utils/PostOverlayContext';
import { StoreOverlayProvider } from '../../utils/StoreOverlayContext';
import AppHeader from './AppHeader';
import LeftSidebar from './LeftSidebar';
import RightSidebar from './RightSidebar';
import BottomTabBar from './BottomTabBar';
import MobileDrawer from './MobileDrawer';
import MessagesOverlay from '../../modals/MessagesOverlay';
import PostOverlay from '../../modals/PostOverlay';
import StoreItemOverlay from '../../modals/StoreItemOverlay';
import NotificationBannerBridge from './NotificationBannerBridge';

function AppLayout({ children }) {
  const dispatch = useDispatch();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [postOverlayId, setPostOverlayId] = useState(null);
  const [storeItemOverlayId, setStoreItemOverlayId] = useState(null);

  useEffect(() => {
    getUserDailyPointStats()
      .then((stats) => dispatch(setDailyStats(stats)))
      .catch(() => {});
  }, [dispatch]);

  const openPost = useCallback((id) => {
    const parsed = typeof id === 'number' ? id : parseInt(id, 10);
    if (!parsed || Number.isNaN(parsed)) return;
    setPostOverlayId(parsed);
  }, []);
  const closePost = useCallback(() => setPostOverlayId(null), []);

  const openStoreItem = useCallback((id) => {
    const parsed = typeof id === 'number' ? id : parseInt(id, 10);
    if (!parsed || Number.isNaN(parsed)) return;
    setStoreItemOverlayId(parsed);
  }, []);
  const closeStoreItem = useCallback(() => setStoreItemOverlayId(null), []);

  return (
    <PostOverlayProvider openPost={openPost} closePost={closePost} postId={postOverlayId}>
      <StoreOverlayProvider
        openStoreItem={openStoreItem}
        closeStoreItem={closeStoreItem}
        itemId={storeItemOverlayId}
      >
        <div className="h-full flex flex-col overflow-hidden bg-void" data-testid="app-layout">
          <AppHeader
            onOpenDrawer={() => setDrawerOpen(true)}
            onOpenMessages={() => setMessagesOpen(true)}
          />
          <div className="flex-1 flex min-h-0 overflow-hidden">
            <NotificationBannerBridge />
            <LeftSidebar />
            <main className="flex-1 min-w-0 overflow-y-auto overscroll-y-contain pb-20 md:pb-0">
              {children ?? <Outlet />}
            </main>
            <RightSidebar />
          </div>
          <BottomTabBar />
          <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
          <MessagesOverlay open={messagesOpen} onClose={() => setMessagesOpen(false)} />
          <PostOverlay postId={postOverlayId} onClose={closePost} />
          <StoreItemOverlay itemId={storeItemOverlayId} onClose={closeStoreItem} />
        </div>
      </StoreOverlayProvider>
    </PostOverlayProvider>
  );
}

export default AppLayout;
